/**
 * A bonus round ([D-024](../../../docs/DECISIONS.md#d-024),
 * [Gamification](../../../docs/GAMIFICATION.md#bonus-rounds)).
 *
 * The only timed screen in the app, and everything about it is built so that
 * the clock is a game rather than a threat:
 *
 * - **It cannot be failed.** The clock runs out and Sneakers says *"you got
 *   seven!"* — there is no pass mark, no red, and a score of zero still pays
 *   forty acorns.
 * - **It can be left at any moment.** "I'm done" finishes the round with what
 *   the child has, and pays for it. A timed screen a child cannot leave is a
 *   trap.
 * - **A wrong answer costs nothing.** No time penalty, no lost points; the
 *   next question simply comes.
 * - **The clock is not the loudest thing on the screen.** It goes quiet and
 *   grey until the last ten seconds, because a number counting down in red for
 *   sixty seconds is the whole experience for an anxious nine-year-old.
 *
 * Which round this is, and what is in it, is decided entirely by the server:
 * this screen asks "what am I playing today" and renders the answer.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { type Client, type Trouble, troubleFrom } from './api.js';
import Sneakers from './Sneakers.js';
import { ExerciseView, type Verdict } from './exercises.js';

/** The last stretch, where the clock earns a bit of urgency. */
const HURRY_SECONDS = 10;

interface Round {
  readonly roundId: string;
  readonly nameL1: string;
  readonly badge: string;
  readonly seconds: number;
  readonly exercises: readonly {
    readonly id: string;
    readonly type: string;
    readonly payload: unknown;
  }[];
}

interface Ending {
  readonly correct: number;
  readonly acorns: number;
  readonly xp: number;
  readonly chest: { readonly tier: string } | null;
}

export default function Bonus({
  client,
  day,
  onLeave,
}: {
  readonly client: Client;
  /** The child's own day, the same one the lesson was finished on. */
  readonly day: string;
  readonly onLeave: () => void;
}) {
  const [round, setRound] = useState<Round | null>(null);
  const [trouble, setTrouble] = useState<Trouble | null>(null);
  const [left, setLeft] = useState<number | null>(null);
  const [at, setAt] = useState(0);
  const [ending, setEnding] = useState<Ending | null>(null);

  /**
   * Answers that were right, in a ref as well as in state.
   *
   * The timer's callback closes over the render it was created in, so a count
   * read from state there would be the count at zero seconds. The ref is what
   * the clock reads.
   */
  const correct = useRef(0);
  /** How many have been answered. See `answered` for why this is not just state. */
  const asked = useRef(0);
  const closing = useRef(false);

  const close = useCallback(async () => {
    // Guarded, because the clock and the "I'm done" button can both fire, and
    // paying twice is the one thing this screen must never ask for.
    if (closing.current) return;
    closing.current = true;
    try {
      const paid = await client.bonus.finish.mutate({ day, correct: correct.current });
      setEnding(
        paid === null
          ? { correct: correct.current, acorns: 0, xp: 0, chest: null }
          : {
              correct: paid.correct,
              acorns: paid.acorns,
              xp: paid.xp,
              chest: paid.chest,
            },
      );
    } catch {
      // The round still happened. Showing what they got, with no payout line,
      // beats an error over a treat — and the server will pay on a retry.
      setEnding({ correct: correct.current, acorns: 0, xp: 0, chest: null });
    }
  }, [client, day]);

  useEffect(() => {
    let live = true;
    // Through `Promise.resolve` because a server older than this app has no
    // `bonus` router and the call *throws* rather than rejecting (D-066).
    Promise.resolve()
      .then(() => client.bonus.start.mutate({ day }))
      .then((started) => {
        if (!live) return;
        if (started === null) {
          // Nothing to play: already done, or the offer is gone. The ordinary
          // screen, not an error.
          onLeave();
          return;
        }
        setRound(started as unknown as Round);
        setLeft(started.seconds);
      })
      .catch((error: unknown) => {
        if (live) setTrouble(troubleFrom(error));
      });
    return () => {
      live = false;
    };
  }, [client, day, onLeave]);

  useEffect(() => {
    if (left === null || ending !== null) return;
    if (left <= 0) {
      void close();
      return;
    }
    const tick = setTimeout(() => {
      setLeft((was) => (was === null ? null : was - 1));
    }, 1000);
    return () => {
      clearTimeout(tick);
    };
  }, [left, ending, close]);

  const answered = useCallback(
    (verdict: Verdict) => {
      if (verdict.kind === 'answered' && verdict.outcome === 'correct') {
        correct.current += 1;
      }
      // Advanced with a ref as well as with state, so the decision below is
      // made here rather than inside the updater: a state updater must be pure,
      // and React is free to run it twice.
      asked.current += 1;
      setAt(asked.current);
      // Ran out of questions before the clock did. That is a finished round,
      // not a broken one.
      if (round !== null && asked.current >= round.exercises.length) void close();
    },
    [round, close],
  );

  if (trouble !== null) {
    return (
      <div className="frame">
        <div className="grow pad center">
          <p className="sub" role="alert">
            The bonus round would not load. Never mind!
          </p>
          <button className="btn" type="button" onClick={onLeave}>
            Back
          </button>
        </div>
      </div>
    );
  }

  if (ending !== null) {
    return (
      <div className="frame">
        <div className="grow center pad">
          <div style={{ fontSize: 32, letterSpacing: 8 }}>✨🎉✨</div>
          {/*
            "You got seven!" — never "you failed", never a pass mark, and never
            a comparison with anybody. There is no score here that can be bad.
          */}
          <h1 style={{ marginTop: 2 }}>You got {ending.correct}!</h1>
          <div style={{ marginTop: 16, width: '100%', textAlign: 'left' }}>
            <Sneakers
              text={
                ending.correct === 0
                  ? 'That is a tricky one. Well played for having a go!'
                  : 'What a round! Look at all that.'
              }
              audioRef={null}
              size={72}
            />
          </div>
          {ending.acorns > 0 && (
            <p className="sub" style={{ marginTop: 12 }}>
              🌰 {ending.acorns} acorns · ⭐ {ending.xp} XP
            </p>
          )}
          {ending.chest !== null && (
            <p className="sub" style={{ marginTop: 4 }}>
              🎁 And a {ending.chest.tier} chest is waiting for you!
            </p>
          )}
          <button
            className="btn"
            type="button"
            style={{ marginTop: 18 }}
            onClick={onLeave}
          >
            Lovely
          </button>
        </div>
      </div>
    );
  }

  if (round === null) {
    return (
      <div className="frame">
        <div className="grow pad center">
          <p className="sub">Getting your bonus round…</p>
        </div>
      </div>
    );
  }

  const current = round.exercises[at];
  const hurry = left !== null && left <= HURRY_SECONDS;

  return (
    <div className="frame">
      <div className="top">
        <span aria-hidden="true" style={{ fontSize: 20 }}>
          {round.badge}
        </span>
        <h3 style={{ flex: 1 }}>{round.nameL1}</h3>
        {/*
          Quiet and grey until the last ten seconds. A number counting down in
          red for a whole minute is the entire experience for an anxious child.
          `aria-live` is off: a clock announced every second is unusable with a
          screen reader.
        */}
        <span
          className="pill"
          aria-label={`${String(left ?? 0)} seconds left`}
          style={{
            fontSize: 13,
            fontWeight: 800,
            color: hurry ? 'var(--orange, #E8843C)' : 'var(--muted-ink, #7C7466)',
          }}
        >
          ⏱ {left ?? 0}
        </span>
      </div>

      {current === undefined ? (
        <div className="grow pad center">
          <p className="sub">That is all of them!</p>
        </div>
      ) : (
        <div className="grow pad">
          <ExerciseView
            // `payload` carries the type; `ExerciseView` parses it and picks
            // the widget, exactly as the lesson runner does.
            exercise={{ exerciseId: current.id, payload: current.payload }}
            onDone={answered}
          />
        </div>
      )}

      {/*
        A timed screen a child cannot leave is a trap. "I'm done" finishes the
        round with whatever they have, and pays for it.
      */}
      <div className="pad" style={{ paddingBottom: 24 }}>
        <button
          type="button"
          className="btn ghost"
          style={{ minHeight: 48 }}
          onClick={() => {
            void close();
          }}
        >
          I’m done
        </button>
      </div>
    </div>
  );
}
